import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { retintEventBackground, useResolvedTheme } from '../theme';
import type { Calendar } from '../types';

export default function CalendarKeywordEditor() {
  const qc = useQueryClient();
  const theme = useResolvedTheme();
  const calsQ = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api.get<Calendar[]>('/api/calendars')
  });

  const writable = (calsQ.data ?? []).filter(
    (c) => c.access_role === 'owner' || c.access_role === 'writer'
  );

  const update = useMutation({
    mutationFn: ({ id, keywords }: { id: string; keywords: string[] }) =>
      api.patch(`/api/calendars/${id}`, { keywords }),
    onMutate: ({ id, keywords }) => {
      qc.setQueryData<Calendar[]>(['calendars'], (old) =>
        old ? old.map((c) => (c.id === id ? { ...c, keywords } : c)) : old
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['calendars'] })
  });

  if (writable.length === 0) {
    return (
      <p className="text-sm text-[var(--text-faint)]">No writable calendars yet.</p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {writable.map((c) => (
        <div
          key={c.id}
          className="rounded-lg border border-[var(--border)] px-3 py-2"
        >
          <div className="mb-2 flex items-center gap-2">
            <span
              className="block h-[12px] w-[12px] flex-shrink-0 rounded-[3px]"
              style={{ background: retintEventBackground(c.background_color, theme) }}
            />
            <span className="text-sm font-medium">{c.summary}</span>
            <span className="text-[0.78rem] text-[var(--text-faint)]">
              {c.account_email}
            </span>
          </div>
          <KeywordChips
            keywords={c.keywords ?? []}
            onChange={(keywords) => update.mutate({ id: c.id, keywords })}
          />
        </div>
      ))}
    </div>
  );
}

function KeywordChips({
  keywords,
  onChange
}: {
  keywords: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  function commit() {
    const cleaned = draft
      .split(/[,\n]/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    if (cleaned.length === 0) return;
    const next = [...keywords];
    for (const kw of cleaned) {
      if (!next.includes(kw)) next.push(kw);
    }
    onChange(next);
    setDraft('');
  }

  function remove(kw: string) {
    onChange(keywords.filter((k) => k !== kw));
  }

  return (
    <div className="flex flex-wrap items-center gap-[6px]">
      {keywords.length === 0 && (
        <span className="text-[0.78rem] text-[var(--text-faint)]">
          No keywords — Quick Add won't auto-select this calendar.
        </span>
      )}
      {keywords.map((kw) => (
        <span
          key={kw}
          className="inline-flex items-center gap-[4px] rounded-full border border-[var(--border)] bg-[var(--bg)] py-[2px] pl-[10px] pr-[4px] text-[0.78rem]"
        >
          {kw}
          <button
            type="button"
            aria-label={`remove keyword ${kw}`}
            onClick={() => remove(kw)}
            className="grid h-[18px] w-[18px] place-items-center rounded-full text-[var(--text-faint)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && draft === '' && keywords.length > 0) {
            e.preventDefault();
            remove(keywords[keywords.length - 1]);
          }
        }}
        onBlur={() => {
          if (draft.trim()) commit();
        }}
        placeholder="add keyword…"
        className="min-w-[80px] flex-1 bg-transparent text-[0.78rem] outline-none placeholder:text-[var(--text-faint)]"
      />
    </div>
  );
}
